import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuthContext } from '../contexts/AuthContext';
import { businessService } from '../services/businessService';
import { Timestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import AdditionalInformationForm from '../components/AdditionalInformationForm';
import { uploadFile, deleteFile } from '../services/storageService';
import { isImageFile, isDocumentFile } from '../utils/imageUtils';
import { upsertFranchiseListing, removeFranchiseListing } from '../services/franchiseService';

interface FormData {
  listingHeadline: string;
  categories: string[];
  generalSummary: string;
  location: string;
  competition: string;
  expansionPotential: string;
  yearsEstablished: string;
  employees: string;
  tradingHours: string;
  supportTraining: string;
  relocatable: 'yes' | 'no' | '';
  workFromHome: boolean;
  franchise: boolean;
  assetSale: boolean;
  financingAvailable: boolean;
  leaseTerms: string;
  reasonForSale: string;
  askingPriceSpecific: string;
  salesRevenueSpecific: string;
  cashFlowSpecific: string;
  websiteAddress: string;
}

const EditListing: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser, loading } = useAuthContext();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedPhotos, setUploadedPhotos] = useState<string[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<string[]>([]);
  
  const [formData, setFormData] = useState<FormData>({
    listingHeadline: '',
    categories: [],
    generalSummary: '',
    location: '',
    competition: '',
    expansionPotential: '',
    yearsEstablished: '',
    employees: '',
    tradingHours: '',
    supportTraining: '',
    relocatable: '',
    workFromHome: false,
    franchise: false,
    assetSale: false,
    financingAvailable: false,
    leaseTerms: '',
    reasonForSale: '',
    askingPriceSpecific: '',
    salesRevenueSpecific: '',
    cashFlowSpecific: '',
    websiteAddress: ''
  });

  usePageTitle('Edit Listing | BizBuyMax.com');

  // Load existing listing
  useEffect(() => {
    const loadListing = async () => {
      if (!id || !currentUser || loading) return;

      try {
        const listing = await businessService.getBusiness(id);
        if (!listing || listing.owner.userId !== currentUser.uid) {
          alert('Error: Listing not found or you do not have permission to edit it.');
          navigate('/account/dashboard');
          return;
        }

        setFormData({
          listingHeadline: listing.basic.title || '',
          categories: listing.basic.tags || [],
          generalSummary: listing.basic.description || '',
          location: (listing.business as any)?.location || '',
          competition: (listing.business as any)?.competition || '',
          expansionPotential: (listing.business as any)?.expansionPotential || '',
          yearsEstablished: listing.business?.established?.toString() || '',
          employees: listing.business?.employees ? `${listing.business.employees.min || 0}-${listing.business.employees.max || 0}` : '',
          tradingHours: (listing.business as any)?.tradingHours || '',
          supportTraining: (listing.business as any)?.supportTraining || '',
          relocatable: listing.business?.relocatable ? 'yes' : (listing.business?.relocatable === false ? 'no' : ''),
          workFromHome: listing.business?.workFromHome || false,
          franchise: listing.business?.franchise || false,
          assetSale: (listing.business as any)?.assetSale || false,
          financingAvailable: listing.financial?.financingAvailable || false,
          leaseTerms: listing.business?.leaseTerms || '',
          reasonForSale: listing.business?.reasonForSale || '',
          askingPriceSpecific: listing.financial.askingPrice?.toString() || '',
          salesRevenueSpecific: listing.financial.annualRevenue?.toString() || '',
          cashFlowSpecific: listing.financial.annualCashFlow?.toString() || '',
          websiteAddress: (listing as any).websiteAddress || ''
        });

        setUploadedPhotos(listing.media.images || []);
        setUploadedDocuments(listing.media.documents || []);
      } catch (error) {
        console.error('Error loading listing:', error);
        alert('Error loading listing. Please try again.');
        navigate('/account/dashboard');
      }
    };

    loadListing();
  }, [id, currentUser, loading, navigate]);

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCategoryChange = (category: string) => {
    setFormData(prev => {
      if (prev.categories.includes(category)) {
        return { ...prev, categories: prev.categories.filter(c => c !== category) };
      } else if (prev.categories.length < 3) {
        return { ...prev, categories: [...prev.categories, category] };
      }
      return prev;
    });
  };

  const removeCategory = (category: string) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter(c => c !== category)
    }));
  };

  const handleRemovePhoto = async (index: number) => {
    if (index < 0 || index >= uploadedPhotos.length) return;

    const photoUrl = uploadedPhotos[index];
    
    try {
      // Remove from state first for immediate UI update
      setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
      
      // Try to delete from Firebase Storage (don't fail if it doesn't exist)
      try {
        await deleteFile(photoUrl);
      } catch (error) {
        console.warn('Failed to delete photo from storage:', error);
        // Continue even if deletion fails
      }
    } catch (error: any) {
      console.error('Error removing photo:', error);
      alert('Failed to remove photo. Please try again.');
      // Re-add to state if removal failed
      setUploadedPhotos(prev => {
        const newPhotos = [...prev];
        newPhotos.splice(index, 0, photoUrl);
        return newPhotos;
      });
    }
  };

  const handleRemoveDocument = async (index: number) => {
    if (index < 0 || index >= uploadedDocuments.length) return;

    const docUrl = uploadedDocuments[index];
    
    try {
      // Remove from state first for immediate UI update
      setUploadedDocuments(prev => prev.filter((_, i) => i !== index));
      
      // Try to delete from Firebase Storage (don't fail if it doesn't exist)
      try {
        await deleteFile(docUrl);
      } catch (error) {
        console.warn('Failed to delete document from storage:', error);
        // Continue even if deletion fails
      }
    } catch (error: any) {
      console.error('Error removing document:', error);
      alert('Failed to remove document. Please try again.');
      // Re-add to state if removal failed
      setUploadedDocuments(prev => {
        const newDocs = [...prev];
        newDocs.splice(index, 0, docUrl);
        return newDocs;
      });
    }
  };

  const handleFileUpload = async (type: 'photo' | 'document') => {
    if (!selectedFile || !id || !currentUser) return;

    // Validate file type
    if (type === 'photo' && !isImageFile(selectedFile)) {
      alert('Please select a valid image file');
      setSelectedFile(null);
      return;
    }

    if (type === 'document' && !isDocumentFile(selectedFile)) {
      alert('Please select a valid document file (PDF, Word, Excel)');
      setSelectedFile(null);
      return;
    }

    // Validate file count limits
    if (type === 'photo' && uploadedPhotos.length >= 15) {
      alert('Maximum 15 photos allowed');
      setSelectedFile(null);
      return;
    }

    if (type === 'document' && uploadedDocuments.length >= 3) {
      alert('Maximum 3 documents allowed');
      setSelectedFile(null);
      return;
    }

    try {
      setIsSaving(true);

      // Determine storage path
      const userId = currentUser.uid;
      const storagePath = type === 'photo' 
        ? `listings/${userId}/${id}/images/`
        : `listings/${userId}/${id}/documents/`;

      // Upload file to Firebase Storage
      const downloadURL = await uploadFile(selectedFile, storagePath, type === 'photo');

      // Update state with download URL
      if (type === 'photo') {
        setUploadedPhotos(prev => [...prev, downloadURL]);
      } else {
        setUploadedDocuments(prev => [...prev, downloadURL]);
      }

      setSelectedFile(null);
    } catch (error: any) {
      console.error('Error uploading file:', error);
      alert(`Failed to upload file: ${error.message || 'Please try again'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const saveListing = async () => {
    if (!id || !currentUser) return;

    try {
      setIsSaving(true);
      const listing = await businessService.getBusiness(id);
      if (!listing || listing.owner.userId !== currentUser.uid) {
        alert('Error: Listing not found or you do not have permission to edit it.');
        setIsSaving(false);
        return;
      }

      // Parse employees field
      let employeesMin = 0;
      let employeesMax = 0;
      if (formData.employees) {
        const employeesMatch = formData.employees.match(/(\d+)\s*-\s*(\d+)/);
        if (employeesMatch) {
          employeesMin = parseInt(employeesMatch[1]);
          employeesMax = parseInt(employeesMatch[2]);
        } else {
          const singleNum = parseInt(formData.employees);
          if (!isNaN(singleNum)) {
            employeesMin = singleNum;
            employeesMax = singleNum;
          }
        }
      }

      const yearsEstablished = formData.yearsEstablished ? parseInt(formData.yearsEstablished) : 0;

      const updateData: any = {
        'basic.title': formData.listingHeadline || listing.basic.title || 'Untitled Listing',
        'basic.description': formData.generalSummary || listing.basic.description || '',
        'basic.shortDescription': (formData.generalSummary || listing.basic.description || '').substring(0, 200),
        'basic.category': formData.categories[0] || listing.basic.category || 'Other',
        'basic.subcategory': formData.categories[1] || listing.basic.subcategory || '',
        'basic.tags': formData.categories.length > 0 ? formData.categories : listing.basic.tags || [],
        'basic.updatedAt': Timestamp.now(),
        'financial.askingPrice': formData.askingPriceSpecific ? parseFloat(formData.askingPriceSpecific) : (listing.financial?.askingPrice || 0),
        'financial.annualRevenue': formData.salesRevenueSpecific ? parseFloat(formData.salesRevenueSpecific) : (listing.financial?.annualRevenue || 0),
        'financial.annualCashFlow': formData.cashFlowSpecific ? parseFloat(formData.cashFlowSpecific) : (listing.financial?.annualCashFlow || 0),
        'financial.financingAvailable': formData.financingAvailable,
        'media.images': uploadedPhotos.length > 0 ? uploadedPhotos : (listing.media?.images || []),
        'media.documents': uploadedDocuments.length > 0 ? uploadedDocuments : (listing.media?.documents || []),
        'media.featuredImage': uploadedPhotos[0] || listing.media?.featuredImage || '',
        'business.location': formData.location || '',
        'business.competition': formData.competition || '',
        'business.expansionPotential': formData.expansionPotential || '',
        'business.established': isNaN(yearsEstablished) ? 0 : yearsEstablished,
        'business.employees.min': employeesMin,
        'business.employees.max': employeesMax,
        'business.tradingHours': formData.tradingHours || '',
        'business.supportTraining': formData.supportTraining || '',
        'business.relocatable': formData.relocatable === 'yes',
        'business.workFromHome': formData.workFromHome,
        'business.franchise': formData.franchise,
        'business.assetSale': formData.assetSale,
        'business.leaseTerms': formData.leaseTerms || '',
        'business.reasonForSale': formData.reasonForSale || '',
        'websiteAddress': formData.websiteAddress || '',
        'search.searchableText': `${formData.listingHeadline || ''} ${formData.generalSummary || ''}`,
        'search.categoryKeywords': formData.categories.length > 0 ? formData.categories : (listing.search?.categoryKeywords || [])
      };

      // Update price range
      const askingPrice = formData.askingPriceSpecific ? parseFloat(formData.askingPriceSpecific) : (listing.financial?.askingPrice || 0);
      let priceRange = 'under_100k';
      if (askingPrice >= 100000 && askingPrice < 500000) priceRange = '100k_500k';
      else if (askingPrice >= 500000 && askingPrice < 1000000) priceRange = '500k_1m';
      else if (askingPrice >= 1000000 && askingPrice < 5000000) priceRange = '1m_5m';
      else if (askingPrice >= 5000000) priceRange = 'over_5m';
      updateData['search.priceRange'] = priceRange;
      updateData['search.revenueRange'] = priceRange;

      const docRef = doc(db, 'businesses', id);
      await updateDoc(docRef, updateData);

      // Sync franchise collection when franchise flag changes
      try {
        if (formData.franchise) {
          await upsertFranchiseListing('businesses', id, {
            title: formData.listingHeadline || listing.basic.title || 'Untitled Listing',
            description: formData.generalSummary || listing.basic.description || '',
            category: formData.categories[0] || listing.basic.category || 'Other',
            subcategory: formData.categories[1] || listing.basic.subcategory || '',
            province: listing.location?.province || '',
            city: listing.location?.city || '',
            askingPrice: formData.askingPriceSpecific ? parseFloat(formData.askingPriceSpecific) : (listing.financial?.askingPrice || 0),
            currency: (listing.financial?.currency as string) || 'CAD',
            annualRevenue: formData.salesRevenueSpecific ? parseFloat(formData.salesRevenueSpecific) : (listing.financial?.annualRevenue || 0),
            annualCashFlow: formData.cashFlowSpecific ? parseFloat(formData.cashFlowSpecific) : (listing.financial?.annualCashFlow || 0),
            featuredImage: uploadedPhotos[0] || listing.media?.featuredImage || listing.media?.images?.[0] || '',
            status: listing.basic.status || 'active',
            createdAt: listing.basic.createdAt || Timestamp.now(),
            updatedAt: Timestamp.now(),
            tags: formData.categories.length > 0 ? formData.categories : listing.basic.tags,
            seoUrl: listing.basic.seoUrl,
          });
        } else {
          await removeFranchiseListing('businesses', id);
        }
      } catch (franchiseErr) {
        console.warn('Franchise collection sync failed (non-blocking):', franchiseErr);
      }

      navigate('/account/dashboard');
    } catch (error: any) {
      console.error('Error saving listing:', error);
      alert('Error saving listing: ' + (error.message || 'Please try again'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!currentUser || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Edit Listing | BizBuyMax.com</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 py-8">
        <div className="max-w-5xl mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Edit Listing</h1>
            <p className="text-gray-600">Update your business listing information</p>
          </div>

          {/* Title and Category Section */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-8 mb-8">
            <div className="flex items-center mb-6">
              <div className="w-2 h-8 bg-gradient-to-b from-primary-500 to-secondary-500 rounded-full mr-4"></div>
              <h3 className="text-xl font-bold text-gray-900">Listing Details</h3>
            </div>
            
            <div className="space-y-8">
              {/* Listing Headline */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Listing Headline *
                </label>
                <input
                  type="text"
                  value={formData.listingHeadline}
                  onChange={(e) => handleInputChange('listingHeadline', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 shadow-sm hover:shadow-md"
                  placeholder="e.g., Profitable and Established Coffee Shop in Devon"
                />
                <p className="text-xs text-gray-600 mt-2">
                  This will be the title of your listing, and it is limited to 8 words.
                </p>
              </div>

              {/* Categories */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Categories *
                </label>
                <div className="border-2 rounded-xl p-4 min-h-[44px] bg-gray-50/50 border-gray-200">
                  {formData.categories.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {formData.categories.map((category) => (
                        <span
                          key={category}
                          className="inline-flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm rounded-lg shadow-sm"
                        >
                          {category}
                          <button
                            onClick={() => removeCategory(category)}
                            className="text-white hover:text-primary-100 transition-colors"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {[
                      'Agricultural Supplies', 'Automotive', 'Bars & Pubs', 'Beauty & Personal Care',
                      'Business Services', 'Childcare', 'Construction', 'Education',
                      'Entertainment', 'Food & Beverage', 'Healthcare',
                      'Home & Garden', 'Manufacturing', 'Online Business', 'Real Estate',
                      'Restaurants', 'Retail', 'Technology', 'Transportation'
                    ].map((category) => (
                      <button
                        key={category}
                        onClick={() => handleCategoryChange(category)}
                        disabled={!formData.categories.includes(category) && formData.categories.length >= 3}
                        className={`text-left px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                          formData.categories.includes(category)
                            ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md'
                            : formData.categories.length >= 3
                            ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                            : 'text-gray-700 hover:bg-white hover:shadow-sm border border-gray-200'
                        }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-600 mt-3 font-medium">
                    You have selected {formData.categories.length}/3 categories
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information Section */}
          <AdditionalInformationForm
            formData={{
              generalSummary: formData.generalSummary,
              location: formData.location,
              competition: formData.competition,
              expansionPotential: formData.expansionPotential,
              yearsEstablished: formData.yearsEstablished,
              employees: formData.employees,
              tradingHours: formData.tradingHours,
              supportTraining: formData.supportTraining,
              relocatable: formData.relocatable,
              workFromHome: formData.workFromHome,
              franchise: formData.franchise,
              assetSale: formData.assetSale,
              financingAvailable: formData.financingAvailable,
              leaseTerms: formData.leaseTerms,
              reasonForSale: formData.reasonForSale,
              askingPriceSpecific: formData.askingPriceSpecific,
              salesRevenueSpecific: formData.salesRevenueSpecific,
              cashFlowSpecific: formData.cashFlowSpecific,
              websiteAddress: formData.websiteAddress
            }}
            onInputChange={(field, value) => handleInputChange(field as any, value)}
            uploadedPhotos={uploadedPhotos}
            uploadedDocuments={uploadedDocuments}
            selectedFile={selectedFile}
            onFileSelect={setSelectedFile}
            onFileUpload={handleFileUpload}
            onRemovePhoto={handleRemovePhoto}
            onRemoveDocument={handleRemoveDocument}
            showIntroduction={false}
          />

          {/* Action Buttons */}
          <div className="flex justify-end gap-4">
            <button
              onClick={() => navigate('/account/dashboard')}
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-300"
            >
              Cancel
            </button>
            <button
              onClick={saveListing}
              disabled={isSaving || !formData.listingHeadline.trim() || formData.categories.length === 0}
              className="px-8 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white inline-block mr-2"></div>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditListing;
